import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  FileText, Printer, CheckCircle2, XCircle, Send, Banknote, Clock,
  AlertCircle, ArrowLeft, ChevronRight, DollarSign, Sparkles,
  QrCode, Copy, Download, X, Pencil, Trash2, Plus,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const STATUS_META: Record<string, { label: string; cor: string; icon: any }> = {
  rascunho: { label: 'Rascunho', cor: 'bg-ink-100 text-ink-700', icon: FileText },
  aguardando_aprovacao: { label: 'Aguardando aprovação', cor: 'bg-amber-100 text-amber-800', icon: Clock },
  aprovado: { label: 'Aprovado', cor: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  rejeitado: { label: 'Rejeitado', cor: 'bg-red-100 text-red-800', icon: XCircle },
  pago: { label: 'Pago', cor: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  cancelado: { label: 'Cancelado', cor: 'bg-ink-200 text-ink-600', icon: XCircle },
}

interface Bordero {
  id: number; numero: number; descricao: string | null
  conta_bancaria_id: number | null
  conta_bancaria_nome: string | null
  forma_pagamento: string
  data_prevista: string | null
  total: number
  status: string
  qtd_itens: number
  criado_por_nome: string | null
  criado_em: string
  aprovado_por_nome: string | null
  aprovado_em: string | null
  pago_em: string | null
  bpm_instancia_id: number | null
  observacao: string | null
}

export default function BorderosPage() {
  const nav = useNavigate()
  const [filtroStatus, setFiltroStatus] = useState<string>('')

  const { data: borderos = [], isLoading } = useQuery<Bordero[]>({
    queryKey: ['borderos', filtroStatus],
    queryFn: async () => {
      const params: any = {}
      if (filtroStatus) params.status = filtroStatus
      return (await api.get('/borderos', { params })).data
    },
    refetchInterval: 30000,
  })

  function imprimir(id: number) {
    const token = localStorage.getItem('pain_token')
    fetch(`/api/borderos/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      })
      .catch(() => toast.error('Erro ao gerar PDF'))
  }

  return (
    <div className="p-4 lg:p-6 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign size={22} className="text-emerald-600" /> Borderôs de Pagamento
          </h1>
          <p className="text-xs text-ink-500">Lote de contas a pagar com fluxo de aprovação BPM</p>
        </div>
        <Link to="/financeiro/pagar" className="text-xs px-3 py-1.5 rounded border border-ink-300 hover:bg-ink-50 flex items-center gap-1.5">
          <FileText size={12} /> Selecionar contas
        </Link>
      </div>

      {/* Filtros de status */}
      <div className="flex flex-wrap gap-1">
        <button onClick={() => setFiltroStatus('')}
          className={`text-xs px-3 py-1 rounded ${
            !filtroStatus ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200'
          }`}>Todos</button>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k} onClick={() => setFiltroStatus(k)}
            className={`text-xs px-3 py-1 rounded ${
              filtroStatus === k ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200'
            }`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-ink-50">
            <tr className="text-[10px] uppercase font-bold text-ink-600">
              <th className="text-left p-2">#</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-left p-2">Banco pagador</th>
              <th className="text-center p-2">Forma</th>
              <th className="text-center p-2">Itens</th>
              <th className="text-right p-2">Total</th>
              <th className="text-left p-2">Criado por</th>
              <th className="text-center p-2">Status</th>
              <th className="text-center p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="p-8 text-center text-ink-400">Carregando...</td></tr>
            ) : borderos.length === 0 ? (
              <tr><td colSpan={9} className="p-12 text-center text-ink-400">
                Nenhum borderô. Vai em Contas a Pagar pra gerar um.
              </td></tr>
            ) : borderos.map(b => {
              const meta = STATUS_META[b.status] || STATUS_META.rascunho
              const Icon = meta.icon
              return (
                <tr key={b.id}
                  onClick={() => nav(`/financeiro/borderos/${b.id}`)}
                  className="border-t border-ink-100 hover:bg-violet-50 cursor-pointer">
                  <td className="p-2 font-mono font-bold">#{b.numero}</td>
                  <td className="p-2 truncate max-w-md">
                    <div className="font-medium">{b.descricao || `Borderô #${b.numero}`}</div>
                    <div className="text-[10px] text-ink-500">
                      {new Date(b.criado_em).toLocaleString('pt-BR')}
                    </div>
                  </td>
                  <td className="p-2">
                    <Banknote size={11} className="inline mr-1 text-ink-400" />
                    {b.conta_bancaria_nome || '—'}
                  </td>
                  <td className="text-center p-2 uppercase text-[10px] font-semibold">{b.forma_pagamento}</td>
                  <td className="text-center p-2 font-mono">{b.qtd_itens}</td>
                  <td className="text-right p-2 font-mono font-bold text-emerald-700">{formatMoney(b.total)}</td>
                  <td className="p-2 text-[10px] truncate max-w-[120px]">{b.criado_por_nome || '—'}</td>
                  <td className="text-center p-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold ${meta.cor}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                  </td>
                  <td className="text-center p-2" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-center">
                      <Link to={`/financeiro/borderos/${b.id}`}
                        className="text-[10px] px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200">
                        Abrir
                      </Link>
                      <button onClick={() => imprimir(b.id)}
                        className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-800 hover:bg-slate-200 flex items-center gap-1"
                        title="Imprimir PDF com 3 assinaturas">
                        <Printer size={10} />
                      </button>
                      {b.bpm_instancia_id && (
                        <Link to={`/bpm/instancias/${b.bpm_instancia_id}`}
                          className="text-[10px] px-2 py-1 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 flex items-center gap-1"
                          title="Ver fluxo BPM">
                          <Sparkles size={10} />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// =================================================
// DETALHE
// =================================================

export function BorderoDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [showRejeitar, setShowRejeitar] = useState(false)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  const [pixModal, setPixModal] = useState<any>(null)
  const [editandoModal, setEditandoModal] = useState(false)
  const [adicionarModal, setAdicionarModal] = useState(false)

  const { data: b, isLoading } = useQuery<any>({
    queryKey: ['bordero', id],
    queryFn: async () => (await api.get(`/borderos/${id}`)).data,
  })

  const aprovar = useMutation({
    mutationFn: async () => (await api.post(`/borderos/${id}/aprovar`)).data,
    onSuccess: () => {
      toast.success('Borderô aprovado')
      qc.invalidateQueries({ queryKey: ['bordero', id] })
      qc.invalidateQueries({ queryKey: ['borderos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const rejeitar = useMutation({
    mutationFn: async () => (await api.post(`/borderos/${id}/rejeitar`, { motivo: motivoRejeicao })).data,
    onSuccess: () => {
      toast.success('Borderô rejeitado')
      setShowRejeitar(false)
      qc.invalidateQueries({ queryKey: ['bordero', id] })
    },
  })
  const marcarPago = useMutation({
    mutationFn: async () => (await api.post(`/borderos/${id}/marcar-pago`)).data,
    onSuccess: (r: any) => {
      toast.success(`${r.pagos} contas baixadas${r.erros > 0 ? ` (${r.erros} com erro)` : ''}`)
      qc.invalidateQueries({ queryKey: ['bordero', id] })
      qc.invalidateQueries({ queryKey: ['borderos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const cancelar = useMutation({
    mutationFn: async () => (await api.post(`/borderos/${id}/cancelar`)).data,
    onSuccess: () => {
      toast.success('Cancelado')
      qc.invalidateQueries({ queryKey: ['bordero', id] })
    },
  })

  const pagarItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.post(`/borderos/${id}/itens/${itemId}/pagar`)).data,
    onSuccess: (r: any) => {
      toast.success(r.bordero_pago_completo
        ? 'Item pago — borderô completo!'
        : 'Item pago')
      qc.invalidateQueries({ queryKey: ['bordero', id] })
      qc.invalidateQueries({ queryKey: ['borderos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const gerarPix = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.get(`/borderos/${id}/itens/${itemId}/pix-brcode`)).data,
    onSuccess: (data, itemId) => setPixModal({ ...data, item_id: itemId }),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar PIX'),
  })

  const removerItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.delete(`/borderos/${id}/itens/${itemId}`)).data,
    onSuccess: () => {
      toast.success('Item removido')
      qc.invalidateQueries({ queryKey: ['bordero', id] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  function baixarCSV() {
    const token = localStorage.getItem('pain_token')
    fetch(`/api/borderos/${id}/csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `bordero-${id}.csv`
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      })
  }

  function imprimir() {
    const token = localStorage.getItem('pain_token')
    fetch(`/api/borderos/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
      })
  }

  if (isLoading || !b) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  const meta = STATUS_META[b.status] || STATUS_META.rascunho
  const Icon = meta.icon

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/financeiro/borderos')} className="p-1.5 rounded hover:bg-ink-100">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
            <DollarSign size={22} className="text-emerald-600" />
            Borderô #{b.numero}
            <span className={`text-xs px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${meta.cor}`}>
              <Icon size={11} /> {meta.label}
            </span>
          </h1>
          <p className="text-xs text-ink-500">{b.descricao}</p>
        </div>
        <div className="flex gap-1">
          <button onClick={imprimir} className="btn-secondary text-xs flex items-center gap-1.5">
            <Printer size={12} /> Borderô PDF
          </button>
          <button onClick={async () => {
              const ids = (b.itens || []).map((it: any) => it.conta_financeira_id)
              if (!ids.length) return
              const token = localStorage.getItem('pain_token')
              const t = toast.loading('Gerando PDF...')
              try {
                const r = await fetch('/api/financeiro/contas/imprimir-guias-pdf', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ids }),
                })
                toast.dismiss(t)
                if (!r.ok) { toast.error('Erro ao gerar guias'); return }
                const blob = await r.blob()
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `bordero-${b.numero}-guias.pdf`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                setTimeout(() => URL.revokeObjectURL(url), 60000)
                toast.success('PDF baixado')
              } catch (e: any) {
                toast.dismiss(t)
                toast.error('Falha: ' + (e.message || 'erro'))
              }
            }}
            className="btn-secondary text-xs flex items-center gap-1.5"
            title="PDF com 1 página por guia, código de barras incluso">
            <Printer size={12} /> Imprimir guias
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Bloco label="Banco pagador" valor={b.conta_bancaria_nome || '—'} />
        <Bloco label="Forma" valor={b.forma_pagamento.toUpperCase()} />
        <Bloco label="Total" valor={formatMoney(b.total)} cor="emerald" />
        <Bloco label="Itens" valor={String(b.itens?.length || 0)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <div><strong className="text-ink-700">Criado:</strong> {new Date(b.criado_em).toLocaleString('pt-BR')} por {b.criado_por_nome || '—'}</div>
        {b.aprovado_em && <div><strong className="text-ink-700">Aprovado:</strong> {new Date(b.aprovado_em).toLocaleString('pt-BR')} por {b.aprovado_por_nome || '—'}</div>}
        {b.pago_em && <div><strong className="text-emerald-700">Pago:</strong> {new Date(b.pago_em).toLocaleString('pt-BR')}</div>}
      </div>

      {b.rejeitado_motivo && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
          <strong className="text-red-900">Motivo da rejeição:</strong>
          <div className="text-red-800 whitespace-pre-wrap mt-1">{b.rejeitado_motivo}</div>
        </div>
      )}

      {/* Botões de ação */}
      <div className="flex flex-wrap gap-2 sticky top-0 bg-ink-50 -mx-4 px-4 py-2 z-10 border-y border-ink-200">
        {!['pago', 'cancelado'].includes(b.status) && (
          <>
            <button onClick={() => setEditandoModal(true)}
              className="btn-secondary text-xs flex items-center gap-1.5">
              <Pencil size={12} /> Editar
            </button>
            <button onClick={() => setAdicionarModal(true)}
              className="btn-secondary text-xs flex items-center gap-1.5">
              <Plus size={12} /> Adicionar contas
            </button>
          </>
        )}
        {b.status === 'aguardando_aprovacao' && (
          <>
            <button onClick={() => aprovar.mutate()} disabled={aprovar.isPending}
              className="btn-primary bg-emerald-600 hover:bg-emerald-700 text-xs flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Aprovar pagamento
            </button>
            <button onClick={() => setShowRejeitar(true)}
              className="btn-secondary bg-red-50 text-red-700 border-red-200 hover:bg-red-100 text-xs flex items-center gap-1.5">
              <XCircle size={12} /> Rejeitar
            </button>
          </>
        )}
        {b.status === 'aprovado' && (
          <button onClick={() => { if (confirm('Confirmar pagamento? Vai criar lançamentos e baixar as contas.')) marcarPago.mutate() }}
            disabled={marcarPago.isPending}
            className="btn-primary text-xs flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Marcar como pago
          </button>
        )}
        {!['pago', 'cancelado'].includes(b.status) && (
          <button onClick={() => { if (confirm('Cancelar borderô? Itens não pagos voltam pra abertos.')) cancelar.mutate() }}
            className="text-xs px-3 py-1.5 rounded text-red-600 hover:bg-red-50 flex items-center gap-1.5">
            <XCircle size={12} /> Cancelar borderô
          </button>
        )}
        {b.bpm_instancia_id && (
          <Link to={`/bpm/instancias/${b.bpm_instancia_id}`}
            className="text-xs px-3 py-1.5 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 flex items-center gap-1.5">
            <Sparkles size={12} /> Ver no BPM
          </Link>
        )}
      </div>

      {/* Itens */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-ink-700 uppercase">Itens do borderô</h3>
        <button onClick={baixarCSV} className="text-xs px-2 py-1 rounded border border-ink-300 hover:bg-ink-50 flex items-center gap-1">
          <Download size={11} /> Exportar CSV
        </button>
      </div>
      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-ink-50">
            <tr className="text-[10px] uppercase font-bold text-ink-600">
              <th className="text-left p-2">Fornecedor</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-left p-2">NF</th>
              <th className="text-center p-2">Vencimento</th>
              <th className="text-right p-2">Valor</th>
              <th className="text-center p-2">Status</th>
              <th className="text-center p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(b.itens || []).map((it: any) => (
              <tr key={it.id} className="border-t border-ink-100">
                <td className="p-2 truncate max-w-[180px]">{it.fornecedor || '—'}</td>
                <td className="p-2 truncate max-w-md">{it.descricao || '—'}</td>
                <td className="p-2 font-mono text-[10px]">{it.numero_doc || '—'}</td>
                <td className="text-center p-2 text-[10px]">
                  {it.vencimento ? new Date(it.vencimento).toLocaleDateString('pt-BR') : '—'}
                </td>
                <td className="text-right p-2 font-mono font-semibold">{formatMoney(it.valor)}</td>
                <td className="text-center p-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                    it.status === 'pago' ? 'bg-emerald-100 text-emerald-800'
                      : it.status === 'falhou' ? 'bg-red-100 text-red-800'
                      : 'bg-ink-100 text-ink-700'
                  }`}>{it.status}</span>
                  {it.erro && <div className="text-[9px] text-red-600 mt-0.5">{it.erro}</div>}
                </td>
                <td className="text-center p-2">
                  <div className="flex gap-1 justify-center">
                    {b.status === 'aprovado' && it.status !== 'pago' && (
                      <>
                        <button onClick={() => gerarPix.mutate(it.id)}
                          disabled={gerarPix.isPending}
                          className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 flex items-center gap-1"
                          title="Gera PIX Copia e Cola pra pagar no app do banco">
                          <QrCode size={10} /> PIX
                        </button>
                        <button onClick={() => { if (confirm(`Marcar item de R$ ${it.valor} como pago?`)) pagarItem.mutate(it.id) }}
                          className="text-[10px] px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 flex items-center gap-1"
                          title="Marca como pago manualmente (após pagar no banco)">
                          <CheckCircle2 size={10} /> Pago
                        </button>
                      </>
                    )}
                    {it.status !== 'pago' && ['rascunho', 'aguardando_aprovacao', 'rejeitado', 'aprovado'].includes(b.status) && (
                      <button onClick={() => pedirExclusao({ descricao: `este item do borderô${it.fornecedor ? ` (${it.fornecedor})` : ''}`, onConfirm: () => removerItem.mutate(it.id) })}
                        className="text-[10px] px-2 py-1 rounded text-red-600 hover:bg-red-50"
                        title="Remover item">
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-ink-50">
            <tr className="font-bold">
              <td colSpan={4} className="p-2 text-right">Total:</td>
              <td className="text-right p-2 font-mono text-emerald-700 text-base">{formatMoney(b.total)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Modal PIX */}
      {pixModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <QrCode size={16} className="text-emerald-600" /> PIX — {pixModal.fornecedor}
              </h3>
              <button onClick={() => setPixModal(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-center bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-xs text-emerald-700 uppercase font-semibold">Valor</div>
                <div className="text-3xl font-bold font-mono text-emerald-900">{formatMoney(pixModal.valor)}</div>
              </div>
              <div className="flex justify-center">
                <img src={`data:image/png;base64,${pixModal.qr_base64}`} alt="QR PIX"
                  className="w-64 h-64 border-2 border-ink-200 rounded" />
              </div>
              <div>
                <label className="label">Copia e cola PIX</label>
                <textarea readOnly className="input text-[10px] font-mono min-h-[80px] bg-ink-50"
                  value={pixModal.brcode} onClick={(e) => (e.target as HTMLTextAreaElement).select()} />
              </div>
              <div className="text-[11px] text-ink-600 bg-blue-50 border border-blue-200 rounded p-2">
                <strong>Fornecedor:</strong> {pixModal.fornecedor}<br />
                <strong>Chave PIX ({pixModal.tipo_chave || 'tipo'}):</strong> {pixModal.chave_pix}<br />
                <strong>ID Transação:</strong> <span className="font-mono">{pixModal.txid}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => {
                  navigator.clipboard.writeText(pixModal.brcode)
                  toast.success('Copiado!')
                }} className="btn-secondary text-xs flex items-center gap-1.5 justify-center">
                  <Copy size={12} /> Copiar código
                </button>
                <button onClick={() => {
                  if (confirm('Confirmar que pagou esse item?')) {
                    pagarItem.mutate(pixModal.item_id)
                    setPixModal(null)
                  }
                }} className="btn-primary bg-emerald-600 hover:bg-emerald-700 text-xs flex items-center gap-1.5 justify-center">
                  <CheckCircle2 size={12} /> Já paguei
                </button>
              </div>
              <div className="text-[10px] text-ink-500 text-center">
                Cole no app do banco ou escaneie o QR. Depois clique "Já paguei" pra registrar.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar */}
      {editandoModal && <EditarBorderoModal bordero={b} onClose={() => setEditandoModal(false)}
        onDone={() => { setEditandoModal(false); qc.invalidateQueries({ queryKey: ['bordero', id] }) }} />}

      {/* Modal adicionar contas */}
      {adicionarModal && <AdicionarContasModal borderoId={Number(id)} jaIncluidos={(b.itens || []).map((it: any) => it.conta_financeira_id)}
        onClose={() => setAdicionarModal(false)}
        onDone={() => { setAdicionarModal(false); qc.invalidateQueries({ queryKey: ['bordero', id] }) }} />}

      {/* Modal rejeitar */}
      {showRejeitar && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold">Rejeitar borderô #{b.numero}</h3>
            </div>
            <div className="p-5 space-y-3">
              <label className="label">Motivo da rejeição</label>
              <textarea className="input min-h-[100px]" value={motivoRejeicao}
                onChange={e => setMotivoRejeicao(e.target.value)}
                placeholder="Explique pra equipe corrigir..." />
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setShowRejeitar(false)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => rejeitar.mutate()} disabled={!motivoRejeicao.trim() || rejeitar.isPending}
                className="btn-primary bg-red-600 hover:bg-red-700 flex-1">Rejeitar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EditarBorderoModal({ bordero, onClose, onDone }: any) {
  const [form, setForm] = useState({
    descricao: bordero.descricao || '',
    conta_bancaria_id: bordero.conta_bancaria_id || '',
    forma_pagamento: bordero.forma_pagamento || 'pix',
    data_prevista: bordero.data_prevista || '',
    observacao: bordero.observacao || '',
  })
  const { data: contasBancarias = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  const salvar = useMutation({
    mutationFn: async () => (await api.put(`/borderos/${bordero.id}`, {
      ...form,
      conta_bancaria_id: Number(form.conta_bancaria_id),
      data_prevista: form.data_prevista || null,
    })).data,
    onSuccess: () => { toast.success('Borderô atualizado'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Editar borderô #{bordero.numero}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label">Banco pagador</label>
            <select className="input" value={form.conta_bancaria_id}
              onChange={e => setForm({ ...form, conta_bancaria_id: e.target.value })}>
              {contasBancarias.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Forma</label>
              <select className="input" value={form.forma_pagamento}
                onChange={e => setForm({ ...form, forma_pagamento: e.target.value })}>
                <option value="pix">PIX</option>
                <option value="ted">TED/DOC</option>
                <option value="boleto">Boleto</option>
                <option value="dinheiro">Dinheiro</option>
              </select>
            </div>
            <div><label className="label">Data prevista</label>
              <input type="date" className="input" value={form.data_prevista}
                onChange={e => setForm({ ...form, data_prevista: e.target.value })} /></div>
          </div>
          <div><label className="label">Descrição</label>
            <input className="input" value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
          <div><label className="label">Observação</label>
            <textarea className="input min-h-[60px]" value={form.observacao}
              onChange={e => setForm({ ...form, observacao: e.target.value })} /></div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
            className="btn-primary flex-1">Salvar</button>
        </div>
      </div>
    </div>
  )
}

function AdicionarContasModal({ borderoId, jaIncluidos, onClose, onDone }: any) {
  const [busca, setBusca] = useState('')
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())

  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-pagar-disponiveis', busca],
    queryFn: async () => {
      const r = await api.get('/financeiro/com-cliente', {
        params: { tipo: 'pagar', status: 'aberto', limit: 500 },
      })
      return r.data.filter((c: any) =>
        !jaIncluidos.includes(c.id)
        && (!busca || (c.descricao || '').toLowerCase().includes(busca.toLowerCase())
          || (c.cliente_nome || '').toLowerCase().includes(busca.toLowerCase()))
      )
    },
  })

  const total = contas.filter((c: any) => selecionados.has(c.id))
    .reduce((a: number, c: any) => a + Math.max(0, Number(c.valor) - Number(c.valor_pago || 0)), 0)

  const adicionar = useMutation({
    mutationFn: async () => (await api.post(`/borderos/${borderoId}/itens`,
      { contas_ids: Array.from(selecionados) })).data,
    onSuccess: (r: any) => { toast.success(`${r.adicionados} conta(s) adicionada(s)`); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Adicionar contas a pagar ao borderô</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-3 border-b">
          <input className="input text-xs" placeholder="Buscar por fornecedor ou descrição..."
            value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(contas, (c: any) => {
              const s = new Set(selecionados)
              s.has(c.id) ? s.delete(c.id) : s.add(c.id)
              setSelecionados(s)
            })} />
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-ink-50 sticky top-0">
              <tr className="text-[10px] uppercase font-bold text-ink-600">
                <th className="w-8 p-2"></th>
                <th className="text-left p-2">Fornecedor</th>
                <th className="text-left p-2">Descrição</th>
                <th className="text-center p-2">Vencimento</th>
                <th className="text-right p-2">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {contas.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-ink-400">Nenhuma conta a pagar disponível</td></tr>
              ) : contas.map((c: any) => {
                const saldo = Math.max(0, Number(c.valor) - Number(c.valor_pago || 0))
                return (
                  <tr key={c.id} className="border-t border-ink-100 hover:bg-blue-50/30 cursor-pointer"
                    onClick={() => {
                      const s = new Set(selecionados)
                      s.has(c.id) ? s.delete(c.id) : s.add(c.id)
                      setSelecionados(s)
                    }}>
                    <td className="p-2"><input type="checkbox" checked={selecionados.has(c.id)} onChange={() => {}} /></td>
                    <td className="p-2 truncate max-w-[180px]">{c.cliente_nome || '—'}</td>
                    <td className="p-2 truncate max-w-md">{c.descricao}</td>
                    <td className="text-center p-2 text-[10px]">{new Date(c.vencimento).toLocaleDateString('pt-BR')}</td>
                    <td className="text-right p-2 font-mono font-semibold">{formatMoney(saldo)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-4 border-t flex items-center justify-between">
          <div className="text-xs">
            {selecionados.size} selecionada(s) · Total: <strong className="font-mono">{formatMoney(total)}</strong>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary text-xs">Cancelar</button>
            <button onClick={() => adicionar.mutate()} disabled={selecionados.size === 0 || adicionar.isPending}
              className="btn-primary text-xs">Adicionar {selecionados.size > 0 ? `(${selecionados.size})` : ''}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Bloco({ label, valor, cor = 'blue' }: any) {
  const cores: any = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  }
  return (
    <div className={`border rounded p-3 ${cores[cor] || cores.blue}`}>
      <div className="text-[10px] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-base font-bold font-mono mt-0.5">{valor}</div>
    </div>
  )
}
